import React, {useId, useRef, useState} from 'react';
import {useLocation, useNavigate} from 'react-router-dom';
import {FaHeart, FaRegHeart} from 'react-icons/fa';
import {Tooltip} from 'react-tooltip';
import {useAuthenticationContext} from '../../providers/AuthenticationProvider';
import './FavoriteButton.css';

const TOOLTIP_TEXT = 'Click the heart to add to your favorites and automatically get notifications about this favorite.';

type PerformerProps = {
    type: 'performer';
    tevoId: number;
    name: string;
};

type VenueProps = {
    type: 'venue';
    tevoId: number;
    name: string;
};

type FavoriteButtonProps = (PerformerProps | VenueProps) & {
    className?: string;
    ariaLabel?: string;
};

const FavoriteButton: React.FC<FavoriteButtonProps> = (props) => {
    const {type, tevoId, name, className, ariaLabel} = props;
    const navigate = useNavigate();
    const location = useLocation();
    const {
        isLoggedIn,
        isPerformerFavorited,
        isVenueFavorited,
        toggleFavoritePerformer,
        toggleFavoriteVenue,
    } = useAuthenticationContext();

    const favorited = type === 'performer'
        ? isPerformerFavorited(tevoId)
        : isVenueFavorited(tevoId);

    const inFlightRef = useRef(false);
    const [, forceUpdate] = useState(0);
    const tooltipId = useId();

    const handleClick = async (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();

        if (!isLoggedIn) {
            const returnTo = encodeURIComponent(location.pathname + location.search);
            navigate(`/login?returnTo=${returnTo}`);
            return;
        }

        if (inFlightRef.current) return;
        inFlightRef.current = true;
        forceUpdate((n) => n + 1);
        try {
            if (type === 'performer') {
                await toggleFavoritePerformer({tevoId, name});
            } else {
                await toggleFavoriteVenue({tevoId, name});
            }
        } finally {
            inFlightRef.current = false;
            forceUpdate((n) => n + 1);
        }
    };

    const label = ariaLabel ?? (favorited ? `Remove ${name} from favorites` : `Add ${name} to favorites`);

    return (
        <>
            <button
                type="button"
                className={`favorite-button ${favorited ? 'favorited' : ''} ${className ?? ''}`}
                onClick={handleClick}
                disabled={inFlightRef.current}
                aria-label={label}
                aria-pressed={favorited}
                data-tooltip-id={tooltipId}
                data-tooltip-content={TOOLTIP_TEXT}
            >
                {favorited ? <FaHeart/> : <FaRegHeart/>}
            </button>
            <Tooltip id={tooltipId} place="top" className="favorite-button-tooltip"/>
        </>
    );
};

export default FavoriteButton;
